/**
 * `<UserHubProvider>` — React context for the singleton `UserHubClient`.
 *
 * Wave 5c.1 wires the realtime fan-out hook into apps/web's inbox: feature
 * components call `useUserHubEvent('message.in', cb)` (no client arg) and
 * the provider resolves the right client. Tests can pass a stub client
 * directly via the `client` prop.
 *
 * Wave 3.1 shipped the bare `UserHubClient` + `useUserHubEvent(client, ...)`
 * variant; that signature is preserved for the extension code path. This
 * file adds the no-arg overload for React consumers.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

import {
  UserHubClient,
  type UserHubClientOptions,
  type UserHubListener,
  type UserHubServerEvent,
} from './ws';

const UserHubContext = createContext<UserHubClient | null>(null);

export interface UserHubProviderProps {
  /** Pre-built client — tests + extension service worker pass this directly. */
  client?: UserHubClient | null;
  /**
   * Lazy options — when no `client` is provided the provider creates one
   * on first mount and tears it down on unmount. Keep options stable
   * (memoise upstream) — identity changes recreate the client.
   */
  options?: UserHubClientOptions;
  /**
   * Connection gate for the auto-managed client (default `true`). While
   * `false` no WebSocket is opened; flipping to `false` tears down an open
   * socket; flipping back to `true` reconnects. Callers gate this on auth
   * state so unauthenticated pages never hit the realtime endpoint. Has no
   * effect on a pre-built `client` (the owner manages its lifecycle).
   */
  enabled?: boolean;
  children: ReactNode;
}

/**
 * Provider that hands a `UserHubClient` down via React context. Either pass
 * a pre-built `client`, or pass `options` to mint one on first mount.
 *
 * Auto-managed clients call `connect()` on mount (while `enabled`) and
 * `close()` on unmount or when `enabled` flips false. Test fixtures should
 * pass `client` directly to avoid the connect side effect.
 */
export function UserHubProvider({
  client: clientOverride,
  options,
  enabled = true,
  children,
}: UserHubProviderProps) {
  const auto = useMemo<UserHubClient | null>(() => {
    if (clientOverride !== undefined) return null;
    if (!options) return null;
    return new UserHubClient(options);
  }, [clientOverride, options]);

  useEffect(() => {
    if (!auto || !enabled) return undefined;
    void auto.connect().catch(() => {
      /* errors surface via opts.onError */
    });
    return () => {
      auto.close(1000, 'provider_unmount');
    };
  }, [auto, enabled]);

  const value = clientOverride ?? auto;
  return <UserHubContext.Provider value={value}>{children}</UserHubContext.Provider>;
}

/**
 * `useUserHub()` — read the singleton from context. Returns `null` when no
 * provider is mounted (callers should treat it as "no realtime").
 */
export function useUserHub(): UserHubClient | null {
  return useContext(UserHubContext);
}

/**
 * `useUserHubChannel(type, cb)` — context-resolved variant of
 * `useUserHubEvent`. Stable across renders even if `cb` identity changes.
 *
 * Returns the resolved client so callers can imperatively `subscribe()` to
 * topics from the same effect (saves a second `useUserHub()` call).
 */
export function useUserHubChannel(type: string, cb: UserHubListener): UserHubClient | null {
  const client = useUserHub();
  const cbRef = useRef(cb);
  cbRef.current = cb;
  const stable = useCallback<UserHubListener>((evt: UserHubServerEvent) => {
    cbRef.current(evt);
  }, []);
  useEffect(() => {
    if (!client) return undefined;
    const unsub = client.on(type, stable);
    return () => {
      unsub();
    };
  }, [client, type, stable]);
  return client;
}
